import React from 'react';
import PropTypes from 'prop-types';

import LanguageConverterV2 from '@careerstart/wae-common/src/main/helperFunction/LanguageConverterV2';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Grid,
  Stack,
  Typography,
} from '@mui/material';

import KeyboardArrowDownIcon from '../../../../assets/icons/KeyboardArrowDownIcon';
import { BLACK, FEED_BACK } from '../../../../theme/colorConstants';
import { PRIMARY_FONT } from '../../../../theme/fontConstants';
import { epochToDateInReadableFormat, epochToTimeInReadableFormat } from '../../../../utils';

import HistoryTimeFields from './HistoryTimeFields';
import {
  ACTION_TYPE_TEXT_KEYS,
  EVENT_TIME_DATA_SECTIONS,
  MODIFIER_TYPES,
  TIME_ACTION_LABEL_KEYS,
  TIMECARD_HISTORY_EVENT_TYPES,
  TIMECARD_HISTORY_STATUS_TEXT_KEYS,
} from './timecardHistoryConstants';

const HistoryCard = ({ event }) => {
  const isModifiedBySystem = event?.modifiedBy?.modifierType === MODIFIER_TYPES.SYSTEM;
  const systemModifierColor = isModifiedBySystem ? FEED_BACK.GREEN_70 : BLACK[100];
  const modifierDisplayName = isModifiedBySystem
    ? LanguageConverterV2('timekeeping.workerTimecard.timecardHistoryCard.modifierType.system')
    : event?.modifiedBy?.name || '--';

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', flexGrow: 1 }}>
      <Accordion
        sx={{
          borderRadius: '5px',
          width: '97%',
          border: `1px solid ${BLACK[60]}`,
        }}
      >
        <AccordionSummary
          aria-controls="history-summary"
          sx={{ pr: '24px', pl: '24px' }}
          expandIcon={<KeyboardArrowDownIcon />}
        >
          <Grid container gap="16px" pr="16px">
            <Grid item xs={3} alignItems="flex-start" flexGrow={1} flexDirection="column">
              <Typography
                style={{
                  fontFamily: PRIMARY_FONT[500],
                  fontSize: '16px',
                }}
              >
                {LanguageConverterV2('timekeeping.workerTimecard.timecardHistoryCard.summaryDate')}
              </Typography>
              <Typography
                style={{
                  fontFamily: PRIMARY_FONT[300],
                  fontSize: '16px',
                }}
              >
                {`${epochToDateInReadableFormat(event?.date)} at ${epochToTimeInReadableFormat(
                  event?.date
                )}`}
              </Typography>
            </Grid>
            <Grid item xs alignItems="flex-start" flexGrow={1} flexDirection="column">
              <Typography
                style={{
                  fontFamily: PRIMARY_FONT[500],
                  fontSize: '16px',
                }}
              >
                {LanguageConverterV2(
                  'timekeeping.workerTimecard.timecardHistoryCard.summaryAction'
                )}
              </Typography>
              <Typography
                style={{
                  fontFamily: PRIMARY_FONT[300],
                  fontSize: '16px',
                }}
              >
                {LanguageConverterV2(ACTION_TYPE_TEXT_KEYS[event?.eventType])}
              </Typography>
            </Grid>
            <Grid item xs={3} alignItems="flex-start" flexGrow={1} flexDirection="column">
              <Typography
                style={{
                  fontFamily: PRIMARY_FONT[500],
                  fontSize: '16px',
                  color: systemModifierColor,
                }}
              >
                {LanguageConverterV2(
                  'timekeeping.workerTimecard.timecardHistoryCard.summaryModifiedBy'
                )}
              </Typography>
              <Typography
                style={{
                  fontFamily: PRIMARY_FONT[300],
                  fontSize: '16px',
                  color: systemModifierColor,
                }}
              >
                {modifierDisplayName}
              </Typography>
            </Grid>
            <Grid item xs alignItems="flex-start" flexGrow={1} flexDirection="column">
              <Typography
                style={{
                  fontFamily: PRIMARY_FONT[500],
                  fontSize: '16px',
                }}
              >
                {LanguageConverterV2(
                  'timekeeping.workerTimecard.timecardHistoryCard.summaryStatus'
                )}
              </Typography>
              <Typography
                style={{
                  fontFamily: PRIMARY_FONT[300],
                  fontSize: '16px',
                }}
              >
                {event?.status
                  ? LanguageConverterV2(TIMECARD_HISTORY_STATUS_TEXT_KEYS[event?.status])
                  : '--'}
              </Typography>
            </Grid>
          </Grid>
        </AccordionSummary>
        <AccordionDetails>
          <Stack gap="22px" pt="16px" pb="16px">
            {EVENT_TIME_DATA_SECTIONS.map((key) => {
              const section = event?.[key];
              if (!section) return null;

              const isClockInEvent = event?.eventType === TIMECARD_HISTORY_EVENT_TYPES.clockIn;
              const isClockOutEvent = event?.eventType === TIMECARD_HISTORY_EVENT_TYPES.clockOut;
              const showClockInAutoPunch = isModifiedBySystem && isClockInEvent;
              const showClockOutAutoPunch = isModifiedBySystem && isClockOutEvent;
              return (
                <HistoryTimeFields
                  key={key}
                  labelKey={TIME_ACTION_LABEL_KEYS[key]}
                  clockInEpoch={section.clockIn}
                  clockOutEpoch={section.clockOut}
                  breakTime={section.break}
                  dailyHours={section.appliedTime}
                  showClockInAutoPunch={showClockInAutoPunch}
                  showClockOutAutoPunch={showClockOutAutoPunch}
                />
              );
            })}
          </Stack>
          {event?.note && (
            <Typography sx={{ padding: '8px' }}>
              {LanguageConverterV2('timekeeping.workerTimecard.timecardHistoryCard.note', {
                note: event?.note,
              })}
            </Typography>
          )}
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

const TimeEntryPropType = PropTypes.shape({
  clockIn: PropTypes.number,
  clockOut: PropTypes.number,
  break: PropTypes.number,
  appliedTime: PropTypes.number,
});

const ModifiedByPropType = PropTypes.shape({
  id: PropTypes.string,
  modifierType: PropTypes.oneOf([MODIFIER_TYPES.USER, MODIFIER_TYPES.SYSTEM]).isRequired,
  name: PropTypes.string,
});

HistoryCard.propTypes = {
  event: PropTypes.shape({
    timecard: TimeEntryPropType,
    proposed: TimeEntryPropType,
    before: TimeEntryPropType,
    after: TimeEntryPropType,
    note: PropTypes.string,
    date: PropTypes.number.isRequired,
    eventType: PropTypes.string.isRequired,
    modifiedBy: ModifiedByPropType.isRequired,
    status: PropTypes.string.isRequired,
  }),
};

export default HistoryCard;
